export interface UserInfo {
  email: string;
  company?: string;
  companyId?: string;
  userId: string;
  permissions: string[];
}

export interface StoredToken {
  token: string;
  expiresAt: number;
  refreshToken?: string;
  user: UserInfo;
}

export class TokenManager {
  private static readonly TOKEN_KEY = 'bifrost_auth_token';
  private static readonly TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutes buffer

  /**
   * Store authentication token in Office roaming settings for cross-document access
   */
  async storeToken(token: string, user: UserInfo, refreshToken?: string): Promise<void> {
    try {
      // Decode JWT to get expiration time
      const payload = this.decodeJWT(token);
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      
      const storedToken: StoredToken = {
        token,
        expiresAt,
        refreshToken,
        user
      };
      
      // Store in Office roaming settings (syncs across all Excel documents)
      await this.setRoamingSetting(TokenManager.TOKEN_KEY, JSON.stringify(storedToken));
      
    } catch (error) {
      console.error('Failed to store token:', error);
      throw new Error('Failed to store authentication token');
    }
  }

  /**
   * Retrieve authentication token from Office roaming settings
   */
  async getToken(): Promise<string | null> {
    try {
      const storedData = await this.getRoamingSetting(TokenManager.TOKEN_KEY);
      
      if (!storedData) {
        return null;
      }
      
      const storedToken: StoredToken = JSON.parse(storedData);

      // Expired (or within the renewal buffer): return null but DO NOT clear.
      // The stored entry — crucially the refresh token — must survive so that
      // AuthService.isAuthenticated() can silently renew the session. Clearing
      // here previously destroyed the refresh token, leaving custom functions
      // permanently #SIGN_IN for a still-renewable, signed-in user.
      if (this.isTokenExpired(storedToken.token)) {
        return null;
      }

      return storedToken.token;
      
    } catch (error) {
      console.error('Failed to retrieve token:', error);
      return null;
    }
  }

  /**
   * Get user information from stored token
   */
  async getUserInfo(): Promise<UserInfo | null> {
    try {
      const storedData = await this.getRoamingSetting(TokenManager.TOKEN_KEY);
      
      if (!storedData) {
        return null;
      }
      
      const storedToken: StoredToken = JSON.parse(storedData);
      const user = storedToken.user;
      if (user?.userId && user.companyId) return user;
      // Sessions from Microsoft sign-in were stored without the company. The
      // access token always carries both ids, and without them the add-in cannot
      // tell whose cached results are whose, so the pane's recalculation refused.
      let claims: { userId?: string; companyId?: string } = {};
      try {
        claims = this.decodeJWT(storedToken.token) as typeof claims;
      } catch {
        return user;
      }
      return { ...user, userId: user?.userId || claims.userId || '', companyId: user?.companyId || claims.companyId };
      
    } catch (error) {
      console.error('Failed to retrieve user info:', error);
      return null;
    }
  }

  /**
   * The stored session as it is kept, or null when there is none: it changes with every sign-in,
   * switch and renewal, which is how one session is told from the next.
   */
  async storedSession(): Promise<string | null> {
    try {
      return (await this.getRoamingSetting(TokenManager.TOKEN_KEY)) || null;
    } catch {
      return null;
    }
  }

  /**
   * Get refresh token if available
   */
  async getRefreshToken(): Promise<string | null> {
    try {
      const storedData = await this.getRoamingSetting(TokenManager.TOKEN_KEY);
      
      if (!storedData) {
        return null;
      }
      
      const storedToken: StoredToken = JSON.parse(storedData);
      return storedToken.refreshToken || null;
      
    } catch (error) {
      console.error('Failed to retrieve refresh token:', error);
      return null;
    }
  }

  /**
   * Clear stored authentication data
   */
  async clearToken(): Promise<void> {
    try {
      await this.removeRoamingSetting(TokenManager.TOKEN_KEY);
    } catch (error) {
      console.error('Failed to clear token:', error);
    }
  }

  /**
   * Check if token is expired (with buffer time)
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeJWT(token);
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      // Consider token expired if it expires within the buffer time
      return currentTime + TokenManager.TOKEN_EXPIRY_BUFFER >= expirationTime;
      
    } catch (error) {
      console.error('Failed to decode token:', error);
      return true; // Treat as expired if we can't decode
    }
  }

  /**
   * Decode JWT token (client-side, for expiration checking and reading claims).
   */
  decodeJWT(token: string): { exp: number; [key: string]: unknown } {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }
      
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
      
    } catch {
      throw new Error('Failed to decode JWT token');
    }
  }

  /**
   * Storage methods now use `OfficeRuntime.storage` instead of
   * `Office.context.roamingSettings`. Reasons:
   *
   * 1. Cross-runtime by design — works between the task pane and the
   *    Custom Functions worker. roamingSettings did not reliably sync
   *    on Excel for Mac in fresh workbooks, leaving custom functions
   *    unauthenticated (cells returned #SIGN_IN even though the task
   *    pane showed signed-in state). Local dev hit this every time.
   *
   * 2. Per-Office-account scope, not per-workbook. Token persists
   *    across workbooks — better UX than re-signing in every new file.
   *
   * 3. Fully async — saveAsync isn't needed; OfficeRuntime.storage
   *    handles persistence internally.
   *
   * Fallback to localStorage kept for browser-style debugging only
   * (Office.js loaded but no addin host).
   */

  private getStorage(): { setItem: (k: string, v: string) => Promise<void>; getItem: (k: string) => Promise<string | null>; removeItem: (k: string) => Promise<void> } | null {
    if (typeof OfficeRuntime === 'undefined') return null;
    const s = OfficeRuntime.storage;
    if (!s) return null;
    return s;
  }

  private async setRoamingSetting(key: string, value: string): Promise<void> {
    const storage = this.getStorage();
    if (storage) {
      await storage.setItem(key, value);
      return;
    }
    localStorage.setItem(`bifrost_${key}`, value);
  }

  private async getRoamingSetting(key: string): Promise<string | null> {
    const storage = this.getStorage();
    if (storage) {
      const value = await storage.getItem(key);
      return value || null;
    }
    return localStorage.getItem(`bifrost_${key}`);
  }

  private async removeRoamingSetting(key: string): Promise<void> {
    const storage = this.getStorage();
    if (storage) {
      await storage.removeItem(key);
      return;
    }
    localStorage.removeItem(`bifrost_${key}`);
  }
}